import { Fragment, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import {
  EVIDENCE_POLICY_LABELS,
  EVIDENCE_POLICY_SLOTS,
  EvidencePolicySlot,
  EvidencePolicySource,
  ReturnAddress,
  StorefrontStaticAsset,
  StorefrontSummary,
  imageUploadError,
} from "../../types/evidence";
import {
  DOMAIN_MESSAGE,
  EMAIL_MESSAGE,
  domainMessage,
  emailMessage,
  requiredPolicySlots,
} from "../../utils/storefrontFields";
import { EMPTY_ADDRESS, addressIncomplete } from "../../utils/returnAddress";
import SettingRow from "../common/SettingRow";
import StorefrontAssetControl, { ASSETS, AssetSpec, CONTROL_WIDTH } from "./StorefrontAssetControl";
import StorefrontDeleteButton from "./StorefrontDeleteButton";
import StorefrontPolicySlot from "./StorefrontPolicySlot";

type SavedField = "name" | "domain" | "contact" | "address" | "tags" | "notes" | "digital";

const tagsText = (storefront: StorefrontSummary) => (storefront.renewal_order_tags ?? []).join(", ");
const notesText = (storefront: StorefrontSummary) => (storefront.checkout_notes ?? []).join("\n");
// Spread over the empty one so the fields keep their order whatever order the saved keys come back in.
const addressOf = (storefront: StorefrontSummary): ReturnAddress => ({ ...EMPTY_ADDRESS, ...storefront.return_address });
const sameAddress = (a: ReturnAddress, b: ReturnAddress) => (Object.keys(EMPTY_ADDRESS) as (keyof ReturnAddress)[]).every((key) => a[key] === b[key]);

function ShopifySection({ storefrontId, onSeeded, onError }: {
  storefrontId: number;
  onSeeded: (storefrontId: number) => void;
  onError: (message: string) => void;
}) {
  const { post } = useApi();

  const [seeding, setSeeding] = useState(false);
  const [seedNotice, setSeedNotice] = useState<{ severity: "success" | "info" | "warning"; message: string } | null>(null);

  const seedPolicies = async () => {
    setSeeding(true);
    setSeedNotice(null);
    try {
      const result = await post<{ seeded: string[]; missingScope: boolean; storefrontId: number | null }>(
        `/api/storefronts/${storefrontId}/seed-policies`,
        {},
        { slowApi: true },
      );
      if (result.missingScope) {
        setSeedNotice({
          severity: "warning",
          message: "This store never granted the read_legal_policies scope. Reauthorize the Chargemont app in Shopify, then seed again.",
        });
      } else if (result.seeded.length === 0) {
        setSeedNotice({
          severity: "info",
          message: "Shopify published no policies to seed — the stored policies are unchanged.",
        });
      } else {
        setSeedNotice({ severity: "success", message: `Seeded ${result.seeded.join(", ")}.` });
      }
      // A seed can take the domain over from a stub row and land the policies on another row,
      // so the whole list is reloaded and the row the API names is the one that opens.
      if (result.storefrontId !== null) onSeeded(result.storefrontId);
    } catch (err: any) {
      onError(err?.message || "Failed to seed policies from Shopify");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <>
      {seedNotice && <Alert severity={seedNotice.severity} sx={{ my: 2 }}>{seedNotice.message}</Alert>}
      <SettingRow
        label="Seed policies from Shopify"
        description="Copies the store's published policies into this storefront. Needs the app scope read_legal_policies; if the store never granted it, reauthorize the app in Shopify and seed again."
        control={
          <Button
            variant="outlined"
            size="small"
            onClick={seedPolicies}
            disabled={seeding}
            startIcon={seeding ? <CircularProgress size={14} /> : null}
          >
            Seed policies from Shopify
          </Button>
        }
      />
    </>
  );
}

export default function StorefrontEditor({ storefront, onChange, onDeleted, onSeeded, onSaved }: {
  storefront: StorefrontSummary;
  onChange: (next: StorefrontSummary) => void;
  onDeleted: () => void;
  onSeeded: (storefrontId: number) => void;
  onSaved?: (message: string) => void;
}) {
  const { put, postForm, del } = useApi();

  const storefrontId = storefront.storefront_id;
  const isShopify = storefront.shopify_account_id !== null;

  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(storefront.name);
  const [domain, setDomain] = useState(storefront.domain ?? "");
  const [contactEmail, setContactEmail] = useState(storefront.contact_email ?? "");
  const [returnAddress, setReturnAddress] = useState(() => addressOf(storefront));
  const [renewalTags, setRenewalTags] = useState(() => tagsText(storefront));
  const [checkoutNotes, setCheckoutNotes] = useState(() => notesText(storefront));
  const [savingField, setSavingField] = useState<SavedField | null>(null);
  // A 400 the API answers about one field reads next to that field, not in the alert above.
  const [rejected, setRejected] = useState<Partial<Record<SavedField, string>>>({});
  const [switchingSlot, setSwitchingSlot] = useState<EvidencePolicySlot | null>(null);
  const [openSlot, setOpenSlot] = useState<EvidencePolicySlot | null>(null);
  const [busyKind, setBusyKind] = useState<AssetSpec["kind"] | null>(null);

  // Reseeded when the row itself was rewritten (a seed): reseeding on every onChange would
  // discard whatever is half-typed, so only untouched inputs follow the row.
  const seeded = useRef({ updatedAt: storefront.updated_at, row: storefront });
  useEffect(() => {
    const previous = seeded.current;
    if (previous.updatedAt === storefront.updated_at) return;
    seeded.current = { updatedAt: storefront.updated_at, row: storefront };
    const before = previous.row;
    setName((current) => (current === before.name ? storefront.name : current));
    setDomain((current) => (current === (before.domain ?? "") ? storefront.domain ?? "" : current));
    setContactEmail((current) => (
      current === (before.contact_email ?? "") ? storefront.contact_email ?? "" : current
    ));
    setReturnAddress((current) => (sameAddress(current, addressOf(before)) ? addressOf(storefront) : current));
    setRenewalTags((current) => (current === tagsText(before) ? tagsText(storefront) : current));
    setCheckoutNotes((current) => (current === notesText(before) ? notesText(storefront) : current));
  }, [storefront]);

  const savedPolicies = storefront.policies;
  const staticAssets = storefront.static_assets ?? [];

  const clearRejected = (field: SavedField) => {
    setRejected((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  const saveField = async (
    field: SavedField,
    body: Record<string, unknown>,
    message: string,
    failure: string,
    fieldMessage?: string,
  ) => {
    if (savingField) return;
    setSavingField(field);
    try {
      const { storefront: saved } = await put<{ storefront: StorefrontSummary }>(`/api/storefronts/${storefrontId}`, body);
      onChange(saved);
      // Only the saved field is re-read: resetting the others would discard what is half-typed there.
      if (field === "name") setName(saved.name);
      if (field === "domain") setDomain(saved.domain ?? "");
      if (field === "contact") setContactEmail(saved.contact_email ?? "");
      if (field === "address") setReturnAddress(addressOf(saved));
      if (field === "tags") setRenewalTags(tagsText(saved));
      if (field === "notes") setCheckoutNotes(notesText(saved));
      setError(null);
      clearRejected(field);
      onSaved?.(message);
    } catch (err: any) {
      // The API's own reason wins when it names one ("not its myshopify address"); the generic
      // "Validation failed" is replaced by the field's wording.
      const specific = err?.message && err.message !== "Validation failed" ? err.message : null;
      // A 409 is always about the domain: another storefront of the company holds it.
      const aboutField = (err?.status === 400 && fieldMessage) || (err?.status === 409 && field === "domain");
      if (aboutField) setRejected((current) => ({ ...current, [field]: specific ?? fieldMessage ?? failure }));
      else setError(err?.message || failure);
    } finally {
      setSavingField(null);
    }
  };

  const selectPolicySource = async (slot: EvidencePolicySlot, source: EvidencePolicySource) => {
    if (switchingSlot) return;
    setSwitchingSlot(slot);
    try {
      const { storefront: saved } = await put<{ storefront: StorefrontSummary }>(
        `/api/storefronts/${storefrontId}`,
        { policies: { [slot]: { source } } },
      );
      onChange(saved);
      setError(null);
      onSaved?.(`${EVIDENCE_POLICY_LABELS[slot]} prints the ${source === "shopify" ? "Shopify" : "manual"} text`);
    } catch (err: any) {
      setError(err?.message || `Failed to switch the ${EVIDENCE_POLICY_LABELS[slot].toLowerCase()}`);
    } finally {
      setSwitchingSlot(null);
    }
  };

  const uploadAsset = async (spec: AssetSpec, file: File) => {
    const invalid = imageUploadError(file, `The ${spec.title.toLowerCase()}`);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setBusyKind(spec.kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", spec.kind);
      const { storefront: saved } = await postForm<{ storefront: StorefrontSummary }>(
        `/api/storefronts/${storefrontId}/static-assets`,
        fd,
        { slowApi: true },
      );
      onChange(saved);
      onSaved?.(`${spec.title} uploaded`);
    } catch (err: any) {
      setError(err?.message || `Failed to upload the ${spec.title.toLowerCase()}`);
    } finally {
      setBusyKind(null);
    }
  };

  const removeAsset = async (spec: AssetSpec, asset: StorefrontStaticAsset) => {
    setBusyKind(spec.kind);
    try {
      const { storefront: saved } = await del<{ storefront: StorefrontSummary }>(
        `/api/storefronts/${storefrontId}/static-assets?key=${encodeURIComponent(asset.key)}`,
      );
      onChange(saved);
      setError(null);
      onSaved?.(`${spec.title} removed`);
    } catch (err: any) {
      setError(err?.message || `Failed to remove the ${spec.title.toLowerCase()}`);
    } finally {
      setBusyKind(null);
    }
  };

  // An empty field is not wrong yet, so its message waits until something has been typed.
  const domainFault = (domain.trim() ? domainMessage(domain) : null) ?? rejected.domain ?? null;
  const contactFault = emailMessage(contactEmail) ?? rejected.contact ?? null;
  const addressBlank = Object.values(returnAddress).every((value) => !value.trim());
  const addressField = (key: keyof ReturnAddress, label: string) => (
    <TextField
      label={label}
      value={returnAddress[key]}
      onChange={(event) => setReturnAddress((current) => ({ ...current, [key]: event.target.value }))}
      disabled={savingField === "address"}
      size="small"
      fullWidth
    />
  );

  return (
    <Box sx={{ py: 1 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <SettingRow
        label="Name"
        description="Printed as the merchant name on every document for this storefront."
        control={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: CONTROL_WIDTH }}>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={savingField === "name"}
              size="small"
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              disabled={savingField !== null || !name.trim() || name.trim() === storefront.name}
              onClick={() => saveField("name", { name: name.trim() }, "Name saved", "Failed to save the name")}
              sx={{ flexShrink: 0 }}
            >
              Save
            </Button>
          </Stack>
        }
      />
      <Divider />

      <SettingRow
        label="Domain"
        description="The domain the buyer checked out on. Orders naming it print this storefront."
        control={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: CONTROL_WIDTH }}>
            <TextField
              label="Domain"
              value={domain}
              onChange={(event) => { setDomain(event.target.value); clearRejected("domain"); }}
              disabled={savingField === "domain"}
              size="small"
              fullWidth
              error={domainFault !== null}
              helperText={domainFault ?? (isShopify
                ? "Learned from the shop's primary domain on its first Shopify draft, or set it here when the store sells on its own domain."
                : undefined)}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={
                savingField !== null
                || domainMessage(domain) !== null
                || domain.trim() === (storefront.domain ?? "")
              }
              onClick={() => saveField(
                "domain",
                { domain: domain.trim() },
                "Domain saved",
                "Failed to save the domain",
                DOMAIN_MESSAGE,
              )}
              sx={{ flexShrink: 0 }}
            >
              Save
            </Button>
          </Stack>
        }
      />
      <Divider />

      <SettingRow
        align="flex-start"
        label="Support email"
        description="Printed as the merchant contact when the order has no store address."
        control={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: CONTROL_WIDTH }}>
            <TextField
              label="Support email"
              value={contactEmail}
              onChange={(event) => { setContactEmail(event.target.value); clearRejected("contact"); }}
              disabled={savingField === "contact"}
              size="small"
              fullWidth
              error={contactFault !== null}
              helperText={contactFault ?? undefined}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={
                savingField !== null
                || emailMessage(contactEmail) !== null
                || contactEmail.trim() === (storefront.contact_email ?? "")
              }
              onClick={() => saveField(
                "contact",
                { contact_email: contactEmail.trim() || null },
                "Support email saved",
                "Failed to save the support email",
                EMAIL_MESSAGE,
              )}
              sx={{ flexShrink: 0 }}
            >
              Save
            </Button>
          </Stack>
        }
      />
      <Divider />

      <SettingRow
        align="flex-start"
        label="Return address"
        description="Sent to PayPal with the evidence when a not-as-described case asks the buyer to return the item."
        control={
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: CONTROL_WIDTH }}>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              {addressField("address_line_1", "Address")}
              {addressField("admin_area_2", "City")}
              {addressField("admin_area_1", "State / region")}
              {addressField("postal_code", "Postal code")}
              {addressField("country_code", "Country (2 letters)")}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              disabled={
                savingField !== null
                || (!addressBlank && addressIncomplete(returnAddress))
                || sameAddress(returnAddress, addressOf(storefront))
              }
              onClick={() => saveField(
                "address",
                { return_address: addressBlank ? null : returnAddress },
                "Return address saved",
                "Failed to save the return address",
              )}
              sx={{ flexShrink: 0 }}
            >
              Save
            </Button>
          </Stack>
        }
      />
      <Divider />

      <SettingRow
        label="Digital products"
        description="No shipping: evidence leaves out fulfilment and the shipping policy, and asks for account activity screenshots instead."
        control={
          <Switch
            // MUI's 12px internal padding would leave the track short of the Save buttons' right edge.
            sx={{ mr: -1.5 }}
            checked={storefront.digital}
            disabled={savingField !== null}
            onChange={(event) => saveField(
              "digital",
              { digital: event.target.checked },
              event.target.checked ? "Marked as digital" : "Marked as shipping goods",
              "Failed to save the digital setting",
            )}
            slotProps={{ input: { "aria-label": "Digital products" } }}
          />
        }
      />
      <Divider />

      <SettingRow
        align="flex-start"
        label="Checkout notes"
        description="One sentence per line, printed as bullets above the checkout screenshots."
        control={
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: CONTROL_WIDTH }}>
            <TextField
              label="Checkout notes"
              value={checkoutNotes}
              onChange={(event) => setCheckoutNotes(event.target.value)}
              disabled={savingField === "notes"}
              size="small"
              multiline
              minRows={2}
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              disabled={savingField !== null || checkoutNotes === notesText(storefront)}
              onClick={() => saveField(
                "notes",
                { checkout_notes: checkoutNotes.split("\n").map((note) => note.trim()).filter(Boolean) },
                "Checkout notes saved",
                "Failed to save the checkout notes",
              )}
              sx={{ flexShrink: 0 }}
            >
              Save
            </Button>
          </Stack>
        }
      />
      <Divider />

      {ASSETS.filter((spec) => spec.kind !== "account" || storefront.digital).map((spec) => {
        const asset = staticAssets.find((candidate) => candidate.kind === spec.kind);
        return (
          <Fragment key={spec.kind}>
            <SettingRow
              align="flex-start"
              label={spec.title}
              description={spec.description}
              control={
                <StorefrontAssetControl
                  spec={spec}
                  storefrontId={storefrontId}
                  asset={asset}
                  busy={busyKind === spec.kind}
                  disabled={busyKind !== null}
                  onUpload={(file) => uploadAsset(spec, file)}
                  onRefuse={setError}
                  onRemove={() => { if (asset) removeAsset(spec, asset); }}
                />
              }
            />
            <Divider />
          </Fragment>
        );
      })}

      {isShopify && (
        <>
          <ShopifySection storefrontId={storefrontId} onSeeded={onSeeded} onError={setError} />
          <Divider />
          <SettingRow
            align="flex-start"
            label="Renewal order tags"
            description="Comma-separated Shopify order tags that mark an order as a subscription renewal."
            control={
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: CONTROL_WIDTH }}>
                <TextField
                  label="Renewal order tags"
                  value={renewalTags}
                  onChange={(event) => setRenewalTags(event.target.value)}
                  disabled={savingField === "tags"}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={savingField !== null || renewalTags === tagsText(storefront)}
                  onClick={() => saveField(
                    "tags",
                    { renewal_order_tags: renewalTags.split(",").map((tag) => tag.trim()).filter(Boolean) },
                    "Renewal order tags saved",
                    "Failed to save the renewal order tags",
                  )}
                  sx={{ flexShrink: 0 }}
                >
                  Save
                </Button>
              </Stack>
            }
          />
          <Divider />
        </>
      )}

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>Policies</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Paste the text from the live policy page. The URL prints under the policy.
        </Typography>
      </Box>

      {EVIDENCE_POLICY_SLOTS.map((slot, index) => (
        <Fragment key={slot}>
          {index > 0 && <Divider />}
          <StorefrontPolicySlot
            slot={slot}
            storefrontId={storefrontId}
            policy={savedPolicies?.[slot]}
            required={requiredPolicySlots(storefront).includes(slot)}
            open={openSlot === slot}
            onToggle={() => setOpenSlot(openSlot === slot ? null : slot)}
            switching={switchingSlot !== null}
            onSelectSource={(source) => selectPolicySource(slot, source)}
            onChange={onChange}
            onError={setError}
            onSaved={onSaved}
          />
        </Fragment>
      ))}

      {!isShopify && (
        <>
          <Divider />
          <StorefrontDeleteButton
            storefrontId={storefrontId}
            label={storefront.domain ?? storefront.name}
            onDeleted={onDeleted}
            onError={setError}
            onSaved={onSaved}
          />
        </>
      )}
    </Box>
  );
}
